import { useRef } from "react";
import { styled } from "@pigment-css/react";
import type { DateRangeWindow } from "@acto/genui-components-schema";
import { useLocale, useTranslations } from "@acto/web-common/locales";
import type { OrderInvoiceStatus } from "@acto/gql/graphql";
import {
  KpiValue,
  MetaLabel,
  RowListCard,
  RowListEmptyState,
  RowSkeletonList,
} from "@acto/taskpane/field-sales-2/components/primitives/Card";
import { Skeleton } from "@acto/taskpane/field-sales-2/components/primitives/Skeleton";
import { Divider } from "@acto/taskpane/field-sales-2/components/primitives/Divider";
import { OrderRows, type OrderRowItem } from "@acto/taskpane/field-sales-2/components/patterns/OrderRows";
import {
  LineItems,
  OrderHeading,
  OrderStatusFigures,
  useLineItemGroups,
} from "@acto/taskpane/field-sales-2/components/patterns/order-details";
import { DrillOverlay, useDrillStack } from "@acto/taskpane/field-sales-2/components/patterns/drilldown";
import { useClientOrders } from "@acto/taskpane/field-sales-2/data-access/useClientOrders";
import { type OrderDetails, useOrderDetails } from "@acto/taskpane/field-sales-2/data-access/useOrderDetails";
import { useInfiniteScroll } from "@acto/taskpane/field-sales-2/pages/useInfiniteScroll";
import { formatNumericDate } from "@acto/taskpane/field-sales-2/utils/format";

interface OrderLevel {
  kind: "order";
  /** The order number. */
  name: string;
  orderId: string;
}

interface OrdersLevel {
  kind: "orders";
  name: string;
  dateRange: DateRangeWindow;
  invoiceStatuses?: readonly OrderInvoiceStatus[];
  productId?: string;
  eyebrow?: string;
}

type DrillLevel = OrderLevel | OrdersLevel;

export interface OrderDrillTarget {
  clientId: string;
  root: DrillLevel;
}

const LineItemGroups = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.fieldSalesTwo.spacing[32],
}));

const LineItemGroup = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.fieldSalesTwo.spacing[24],
}));

const OrderLevelContent = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.fieldSalesTwo.spacing[24],
}));

const OrderSummary = styled(OrderLevelContent)(({ theme }) => ({
  paddingInline: theme.fieldSalesTwo.spacing[16],
}));

const ValueBlock = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.fieldSalesTwo.spacing[4],
}));

interface OrderDrillOverlayProps {
  target: OrderDrillTarget;
  onClose: () => void;
}

export function OrderDrillOverlay({ target, onClose }: OrderDrillOverlayProps) {
  const { stack, current, push, drillTo } = useDrillStack<DrillLevel>(target.root, onClose);

  if (current.kind === "orders") {
    return (
      <OrdersDrillLevel
        clientId={target.clientId}
        level={current}
        stack={stack}
        onDrillTo={drillTo}
        onOpenOrder={(order) => push({ kind: "order", name: order.externalReference, orderId: order.id })}
      />
    );
  }
  return <OrderDrillLevel level={current} stack={stack} onDrillTo={drillTo} />;
}

interface LevelProps<T extends DrillLevel> {
  level: T;
  stack: DrillLevel[];
  onDrillTo: (depth: number) => void;
}

function OrdersDrillLevel({
  clientId,
  level,
  stack,
  onDrillTo,
  onOpenOrder,
}: LevelProps<OrdersLevel> & {
  clientId: string;
  onOpenOrder: (order: OrderRowItem) => void;
}) {
  const t = useTranslations("fieldSalesTwo");
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const orders = useClientOrders(
    clientId,
    { startDate: level.dateRange.start, endDate: level.dateRange.end },
    level.invoiceStatuses,
    undefined,
    level.productId,
  );
  const sentinelRef = useInfiniteScroll<HTMLLIElement>({
    hasMore: orders.hasMore,
    isLoadingMore: orders.isLoadingMore,
    loadMore: orders.loadMore,
    resetKey: orders.items.length,
    root: scrollAreaRef,
  });
  const rows = orders.items.map((order) => ({
    ...order,
    statusLabel: order.invoiceStatus ? t(`analysis.orders.invoiceStatus.${order.invoiceStatus}`) : undefined,
  }));

  const isFirstPageLoading = orders.items.length === 0 && orders.isPending;
  const hasFirstPageFailed = orders.error !== undefined && orders.items.length === 0;
  const state = isFirstPageLoading ? "loading" : hasFirstPageFailed ? "error" : orders.isEmpty ? "empty" : "rows";

  return (
    <DrillOverlay eyebrow={level.eyebrow} stack={stack} onDrillTo={onDrillTo} scrollAreaRef={scrollAreaRef}>
      <RowListCard nested transitionKey={state}>
        {state === "loading" && <RowSkeletonList />}
        {state === "error" && <RowListEmptyState>{t("orderDrill.error")}</RowListEmptyState>}
        {state === "empty" && <RowListEmptyState>{t("orderDrill.empty")}</RowListEmptyState>}
        {state === "rows" && (
          <OrderRows items={rows} onOpenOrder={onOpenOrder} sentinelRef={orders.hasMore ? sentinelRef : undefined} />
        )}
      </RowListCard>
    </DrillOverlay>
  );
}

function OrderDrillLevel({ level, stack, onDrillTo }: LevelProps<OrderLevel>) {
  const t = useTranslations("fieldSalesTwo");
  const { locale } = useLocale();
  const { isPending, order } = useOrderDetails(level.orderId);
  const valueLabel = order
    ? `${t("orderDetails.valueLabel")} · ${formatNumericDate(order.date, locale)}`
    : t("orderDetails.valueLabel");
  const value = order ? <KpiValue>{order.formattedIncomingOrder}</KpiValue> : <Skeleton.Line width="45%" height={28} />;
  const isOpenedFromRow = stack[0]?.kind === "order";
  const state = order ? "items" : isPending ? "loading" : "error";

  return (
    <DrillOverlay stack={stack} onDrillTo={onDrillTo}>
      <OrderLevelContent>
        <OrderSummary>
          {isOpenedFromRow && (
            <>
              <OrderHeading>{level.name}</OrderHeading>
              <Divider />
            </>
          )}
          <ValueBlock>
            <MetaLabel>{valueLabel}</MetaLabel>
            {value}
          </ValueBlock>
          {order && <OrderStatusFigures order={order} />}
        </OrderSummary>
        <RowListCard nested transitionKey={state}>
          {state === "loading" && <RowSkeletonList />}
          {state === "error" && <RowListEmptyState>{t("orderDrill.orderError")}</RowListEmptyState>}
          {order && <OrderLineItems order={order} />}
        </RowListCard>
      </OrderLevelContent>
    </DrillOverlay>
  );
}

function OrderLineItems({ order }: { order: OrderDetails }) {
  const groups = useLineItemGroups(order);

  return (
    <LineItemGroups>
      {groups.map((group) => (
        <LineItemGroup key={group.key}>
          <MetaLabel>{group.title}</MetaLabel>
          <LineItems items={group.items} />
        </LineItemGroup>
      ))}
    </LineItemGroups>
  );
}
